import React, { useState } from 'react';
import classnames from 'classnames';
import { MixAndMatchSelector } from 'src/MixAndMatchBundle/MixAndMatchSelector';
import { MixAndMatchNumberInput } from 'src/MixAndMatchBundle/MixAndMatchNumberInput';
import { MixAndMatchImageGallery } from 'src/MixAndMatchBundle/MixAndMatchImageGallery';
import { CamelizedMediaEntry } from 'src/types/global-types';
import { MixAndMatchVariant } from 'src/MixAndMatchBundle/MixAndMatchProductContainer';
import {
    HandleBundleColorChangeType,
    HandleBundleSizeChangeType,
    MixAndMatchValueType,
} from 'src/MixAndMatchBundle/MixAndMatchProductPicker';
import { BundleSizeGuideAndDetails } from 'src/components/BundleProduct/BundleSizeGuideAndDetails';
import { ModalProductInfo } from 'src/components/ProductDetail/ModalProductInfo';
import { DeliveryTab } from 'src/components/ProductDetail/DeliveryTab';
import { BundleTypeValues, BaseBundleType } from 'src/components/BundleProduct/types/index';
import { getCalculatedMediaEntries } from 'src/util/getCalculatedMediaEntries';
import { CamelizedBundleVariants } from 'src/queries/__generated__/wweBundle';
import camelcaseKeys from 'camelcase-keys';
import { WearerOptionMap } from 'src/components/WeareBundleProduct/utils/wearer.types';
import { WearerSummary } from 'src/components/BundleProduct/BundleOptionsProvider';
import { MixAndMatchSizeAndColor } from 'src/MixAndMatchBundle/MixAndMatchSizeAndColor';
import { MixAndMatchSelectorDummy } from 'src/MixAndMatchBundle/MixAndMatchSelectorDummy';
import {
    MAX_QUANTITY_FOR_PRODUCTS,
    MAX_QUANTITY_FOR_PRODUCTS_WITHOUT_LIMIT,
} from 'src/components/ProductDetail/utils/sizeForm';

import classes from './MixAndMatchProductPicker.scss';

export type HandleProductChangeType = (
    id: number,
    productId: number,
    name: string,
    newValue: MixAndMatchValueType,
    isProductPicked: boolean,
    image?: string,
    mediaEntries?: CamelizedMediaEntry[],
) => void;

type Props = {
    onDelete: (id: number) => void;
    id: number;
    productId?: number;
    productName: string;
    productImage?: string;
    productMedia?: CamelizedMediaEntry[];
    value?: MixAndMatchValueType;
    handleBundleColorChange: HandleBundleColorChangeType;
    handleBundleSizeChange: HandleBundleSizeChangeType;
    handleBundleQuantityChange: (quantity: number, index: number, productIndex: number) => void;
    handleProductChange: HandleProductChangeType;
    variants: MixAndMatchVariant[];
    index: number;
    productIndex: number;
    wearerOptions: WearerOptionMap;
    summary: WearerSummary[];
    disabled: boolean;
    colorId?: number;
    handleProductValueChange: (productId: number, value: number, type: 'colorId' | 'maxQty') => void;
    maxQty?: number;
};

export const MixAndMatchPickerRow: React.FC<Props> = ({
    id,
    productId,
    productName,
    productImage,
    value,
    onDelete,
    handleBundleColorChange,
    handleBundleSizeChange,
    handleBundleQuantityChange,
    handleProductChange,
    variants,
    index,
    productIndex,
    productMedia,
    wearerOptions,
    summary,
    disabled,
    colorId,
    handleProductValueChange,
    maxQty,
}) => {
    const isProductPicked = Boolean(value && Object.keys(value).length);
    const [newOptionPicked, setNewOptionPicked] = useState<string | undefined>();
    const [sizeEnabled, setSizeEnabled] = useState(false);
    const [inputEnabled, setInputEnabled] = useState(false);
    const [isOpenProductInfoModal, setIsOpenProductInfoModal] = useState(false);
    const [wearerBundleData, setWearerBundleData] = useState<BundleTypeValues | BaseBundleType | undefined>(undefined);

    const details =
        productName && productId && wearerBundleData ? (wearerBundleData as BundleTypeValues)?.details : undefined;

    const camelizedBundleVariants = camelcaseKeys(((wearerBundleData as BaseBundleType)?.bundleVariants ?? {}) as any, {
        deep: true,
    }) as unknown as CamelizedBundleVariants;

    const additionalMediaEntry =
        colorId && wearerBundleData && camelizedBundleVariants
            ? getCalculatedMediaEntries(colorId, camelizedBundleVariants)
            : undefined;

    return (
        <div className={classes.tableAndInfoContainer}>
            {details && (
                <ModalProductInfo
                    isOpenProductInfoModal={isOpenProductInfoModal}
                    closeProductInfoModal={() => setIsOpenProductInfoModal(false)}
                    description={details.description || ''}
                    is24HourDelivery={details.is24HourDelivery}
                    canCustomise={details.canCustomise}
                    mediaEntires={details.mediaEntries}
                    additionalMediaEntry={additionalMediaEntry}
                    productName={details.name}
                    delivery={<DeliveryTab />}
                />
            )}

            <div className={classnames(classes.table, classes.tableBody)}>
                <div className={classes.infoContainer}>
                    {details && (
                        <BundleSizeGuideAndDetails
                            sizeGuides={details?.sizeGuides}
                            details={details}
                            setIsOpenProductInfoModal={setIsOpenProductInfoModal}
                            alwaysOn
                        />
                    )}
                </div>
                <MixAndMatchImageGallery
                    url={!isProductPicked ? require('../../static/img/transparent-shirt.png') : productImage}
                    isProductPicked={isProductPicked}
                    productMedia={productMedia}
                    additionalMediaEntry={additionalMediaEntry}
                    productName={productName}
                    is24HourDelivery={false}
                    canCustomise={true}
                />

                <MixAndMatchSelector
                    disabled={disabled}
                    productIndex={productIndex}
                    title="Select Product"
                    productVariants={variants}
                    handleProductChange={handleProductChange}
                    index={index}
                    className={classes.productInput}
                    productId={id}
                    setNewOptionPicked={setNewOptionPicked}
                    setInputEnabled={setInputEnabled}
                    setSizeEnabled={setSizeEnabled}
                />

                {productId && productName ? (
                    <MixAndMatchSizeAndColor
                        key={productId}
                        colorDisabled={!isProductPicked}
                        sizeDisabled={!sizeEnabled}
                        colorOptions={value?.colors}
                        sizeOptions={value?.sizes}
                        productIndex={productIndex}
                        handleBundleColorChange={handleBundleColorChange}
                        handleBundleSizeChange={handleBundleSizeChange}
                        index={index}
                        productId={productId}
                        id={id}
                        newOptionPicked={newOptionPicked}
                        setNewOptionPicked={setNewOptionPicked}
                        setInputEnabled={setInputEnabled}
                        setSizeEnabled={setSizeEnabled}
                        handleProductValueChange={handleProductValueChange}
                        wearerOptions={wearerOptions}
                        summary={summary}
                        setWearerBundleData={setWearerBundleData}
                        colorId={colorId}
                        variants={variants}
                    />
                ) : (
                    <>
                        <MixAndMatchSelectorDummy title="Colour" className={classes.colourInput} />
                        <MixAndMatchSelectorDummy title="Size" className={classes.sizeInput} />
                    </>
                )}

                <MixAndMatchNumberInput
                    disabled={!inputEnabled}
                    handleBundleQuantityChange={handleBundleQuantityChange}
                    productIndex={productIndex}
                    index={index}
                    newOptionPicked={newOptionPicked}
                    maxQty={
                        maxQty
                            ? maxQty >= 10000
                                ? MAX_QUANTITY_FOR_PRODUCTS
                                : maxQty
                            : MAX_QUANTITY_FOR_PRODUCTS_WITHOUT_LIMIT
                    }
                />
                <button
                    disabled={!isProductPicked}
                    className={classnames(classes.removeButtonDisabled, {
                        [classes.removeButtonEnabled]: isProductPicked,
                    })}
                    type="button"
                    onClick={() => onDelete(id)}
                >
                    <img
                        className={classes.removeButtonIcon}
                        src={require('../../src/svg/MixAndMatchBin.svg')}
                        alt=""
                    />
                    <span className={classes.removeButtonText}>Remove</span>
                </button>
            </div>
        </div>
    );
};
